import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GlassButton } from "@/components/ui/glass-button";
import { Music, RotateCcw, TableProperties, Sparkles, Radio, Settings, ScrollText } from "lucide-react";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { ConfirmationDialog } from "@/components/confirmation-dialog";
import AddOpenMicParticipantModal from "@/components/add-open-mic-participant-modal";
import UnifiedParticipantsTable from "@/components/unified-participants-table";
import UnifiedOnStageTable from "@/components/unified-on-stage-table";
import ParticipantStats from "@/components/participant-stats";
import RaffleModal from "@/components/raffle-modal";
import { PageBackground } from "@/components/ui/page-background";
import { TrackerHeader } from "@/components/tracker-header";
import type { JamNight, Performer } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import SetTimeLimitModal from "@/components/set-time-limit-modal";
import { EventActionsDropdown } from "@/components/event-actions-dropdown";
import { Button } from "@/components/ui/button.tsx";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useStablePerformers } from "@/hooks/use-stable-performers";
import { EventNotificationSettings } from "@/components/event-notification-settings";

interface OpenMicTrackerProps {
  jamNightId: number;
  activeTab?: string;
}

function OpenMicTrackerContent({ jamNightId, activeTab: urlActiveTab }: OpenMicTrackerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTimeLimitModal, setShowTimeLimitModal] = useState(false);
  const [showRaffleModal, setShowRaffleModal] = useState(false);
  const [showResetAllConfirm, setShowResetAllConfirm] = useState(false);
  const [selectedTab, setSelectedTab] = useState("live");
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();

  // Tab change handler that syncs with URL
  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    setLocation(`/event/${jamNightId}/${value}`);
  };

  // Initialize tab from URL or use default
  useEffect(() => {
    if (urlActiveTab) {
      // Valid tabs for open mic tracker
      const validTabs = ["live", "manage"];
      if (validTabs.includes(urlActiveTab)) {
        setSelectedTab(urlActiveTab);
      } else {
        // Invalid tab in URL, replace URL without creating history entry
        window.history.replaceState(null, '', `/event/${jamNightId}/live`);
        setSelectedTab("live");
      }
    } else {
      // No tab in URL, replace URL without creating history entry
      window.history.replaceState(null, '', `/event/${jamNightId}/live`);
      setSelectedTab("live");
    }
  }, [urlActiveTab, jamNightId]);

  const { data: jamNight, isLoading: jamNightLoading } = useQuery<JamNight>({
    queryKey: [`/api/events/${jamNightId}`],
  });

  const { data: rawPerformers = [], isLoading: performersLoading } = useQuery<
    Performer[]
  >({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
  });

  // Stabilize performers array to prevent unnecessary re-renders of child components
  const performers = useStablePerformers(rawPerformers);

  const resetAllMutation = useMutation({
    mutationFn: async () => {
      // Sort performers alphabetically by name
      const sortedPerformers = [...performers].sort((a, b) => 
        a.name.localeCompare(b.name)
      );

      // Create reset promises with new sort orders
      const resetPromises = sortedPerformers.map((performer, index) =>
        apiRequest("PATCH", `/api/performers/${performer.id}`, {
          played: false,
          timeOnStage: 0,
          status: 'participant',
          stageStartTime: null,
          performanceCount: 0, // Reset performance count
          sortOrder: (index + 1) * 1000, // Assign sort orders with gaps of 1000
        })
      );
      await Promise.all(resetPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
      toast({
        title: "Success",
        description: "All participants have been reset successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reset participants. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onStagePerformers = useMemo(() => 
    performers.filter((p) => p.status === 'on_stage' && !p.removed), [performers]);
  const offStagePerformers = useMemo(() => 
    performers.filter((p) => p.status !== 'on_stage' && !p.removed), [performers]);

  const userRole = useMemo(() => {
    if (user?.role === 'god') return 'god';
    if (!user?.organisationUsers || !jamNight?.organisationId) return undefined;
    return user.organisationUsers.find(
      (ou) => ou.organisation.id === jamNight.organisationId
    )?.role;
  }, [user?.role, user?.organisationUsers, jamNight?.organisationId]);

  const isLoading = jamNightLoading || performersLoading;
  const isEventCompleted = jamNight?.completed || false;

  const RenderManagementSection = () => (
    <>
      <div className="flex justify-center items-center mb-4">
        <h1 className="text-2xl font-bold text-slate-800 drop-shadow-sm text-center">{jamNight?.name}</h1>
      </div>
      <ParticipantStats performers={performers.filter((p) => !p.removed)} />

      {/* Action Buttons */}
      <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
        <GlassButton
          variant="outline"
          boxShadowColor="rgba(220,38,38,0.1)"
          disabled={isEventCompleted || resetAllMutation.isPending || performers.length === 0}
          onClick={() => setShowResetAllConfirm(true)}
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset All Participants
        </GlassButton>
        <ConfirmationDialog
          isOpen={showResetAllConfirm}
          onOpenChange={setShowResetAllConfirm}
          title="Reset All Participants?"
          description="This will reset all participants back to 'not played' status and clear their stage time. Anyone currently on stage will be reset. This action cannot be undone."
          onConfirm={() => resetAllMutation.mutate()}
          confirmText="Yes, Reset All"
          isConfirming={resetAllMutation.isPending}
        />

        {(userRole === 'owner' || userRole === 'god') && (
          <Button
            variant="outline"
            onClick={() => setShowTimeLimitModal(true)}
            disabled={isEventCompleted}
            className="bg-white/40 backdrop-blur-md border-white/30 hover:bg-white/50 shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              boxShadow: '0 4px 16px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
          >
            Set Time Limit
          </Button>
        )}

        <Button
          variant="outline"
          className="bg-blue-100/40 backdrop-blur-md border-blue-200/50 text-blue-700 hover:bg-blue-100/60 hover:border-blue-300/60 shadow-lg transition-all duration-300"
          style={{
            boxShadow: '0 4px 16px rgba(59,130,246,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
          }}
          onClick={() => setLocation(`/event/${jamNightId}/participants`)}
        >
          <TableProperties className="h-4 w-4 mr-2" />
          View All Participant Data
        </Button>

        {jamNight?.raffleEnabled && (
          <Button
            variant="outline"
            className="bg-yellow-100/40 backdrop-blur-md border-yellow-200/50 text-yellow-700 hover:bg-yellow-100/60 hover:border-yellow-300/60 shadow-lg transition-all duration-300"
            style={{
              boxShadow: '0 4px 16px rgba(251,191,36,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
            }}
            onClick={() => setShowRaffleModal(true)}
            disabled={isEventCompleted || performers.filter(p => !p.removed && (p.raffleEntrances || 0) > 0).length === 0}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Run Raffle
          </Button>
        )}
      </div>

      {/* Event Management Dropdown - Full width on mobile */}
      {jamNight && (userRole === 'owner' || userRole === 'god') && (
        <div className="mt-4 sm:flex sm:justify-center">
          <EventActionsDropdown
            jamNight={jamNight}
            canManageEvent={true}
            variant="button"
            className="w-full sm:w-auto"
          />
        </div>
      )}

      {/* Event Feed */}
      <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-lg border border-white/20 p-4 mb-4 mt-4"
        style={{
          background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
        }}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-1 drop-shadow-sm">Event Feed</h3>
            <p className="text-sm text-slate-600">View a chronological feed of all event activities</p>
          </div>
          <Button
            onClick={() => setLocation(`/events/${jamNightId}/feed`)}
            className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg"
            style={{
              boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            <ScrollText className="h-4 w-4 mr-2" />
            View Feed
          </Button>
        </div>
      </div>

      {/* Notification Settings */}
      {jamNight && (
        <EventNotificationSettings eventId={jamNightId} eventName={jamNight.name} />
      )}
    </>
  );

  if (isLoading) {
    return (
      <PageBackground className="flex items-center justify-center">
        <div className="text-center relative z-10">
          <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700 drop-shadow-sm" />
          <p className="text-slate-700 drop-shadow-sm">Loading open mic data...</p>
        </div>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      
      <TrackerHeader
        isEventCompleted={isEventCompleted}
        onAddParticipant={() => setShowAddModal(true)}
      />

      <main className="mx-auto px-4 sm:px-6 lg:px-32 xl:px-48 pt-4 pb-6 relative z-10">
          <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-white/30 backdrop-blur-md border border-white/20 shadow-lg p-1">
              <TabsTrigger 
                value="live"
                className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
              >
                <Radio className={`h-4 w-4 ${onStagePerformers.length > 0 ? 'text-green-500 animate-pulse' : ''}`} />
                Live
              </TabsTrigger>
              <TabsTrigger 
                value="manage"
                className="data-[state=active]:bg-white/40 data-[state=active]:text-blue-600 data-[state=active]:font-semibold data-[state=active]:shadow-md transition-all duration-300 flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Manage
              </TabsTrigger>
            </TabsList>

            <TabsContent value="live" className="mt-4 space-y-4">
              {/* Currently On Stage */}
              {onStagePerformers.length > 0 && jamNight && (
                <UnifiedOnStageTable
                  performers={onStagePerformers}
                  jamNightId={jamNightId}
                  jamNight={jamNight}
                  disabled={isEventCompleted}
                  mode="open-mic"
                />
              )}

              {/* All Participants */}
              <UnifiedParticipantsTable
                performers={offStagePerformers}
                jamNightId={jamNightId}
                jamNight={jamNight}
                disabled={isEventCompleted}
                mode="open-mic"
              />
            </TabsContent>

            <TabsContent value="manage" className="mt-4 space-y-4">
              <RenderManagementSection />
            </TabsContent>
          </Tabs>

        {jamNight && (
          <SetTimeLimitModal
            isOpen={showTimeLimitModal}
            onOpenChange={setShowTimeLimitModal}
            jamNight={jamNight}
          />
        )}
      </main>

      <AddOpenMicParticipantModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        jamNightId={jamNightId}
      />

      <RaffleModal
        open={showRaffleModal}
        onOpenChange={setShowRaffleModal}
        performers={performers.filter((p) => !p.removed)}
        jamNightId={jamNightId}
      />
    </PageBackground>
  );
}

export default function OpenMicTracker(props: OpenMicTrackerProps) {
  return <OpenMicTrackerContent {...props} />;
}